import React, { useCallback, useEffect, useState } from "react";
import { IResourceComponentsProps } from "@pankod/refine-core";
import {
  List,
  Table,
  Card,
  Row,
  Col,
  Space,
  Button,
  DatePicker,
  Tag,
  Typography,
  Spin,
} from "@pankod/refine-antd";
import { LeftOutlined, RightOutlined, CoffeeOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import axios from "axios";
import { authProvider } from "authProvider";

const { Text } = Typography;

// axios avec auth + refresh (même pattern que le Liveboard Guesty)
const axiosInstance = axios.create();
axiosInstance.interceptors.request.use((config) => {
  config.headers = {
    Authorization: `Bearer ${localStorage.getItem("ACCESS_TOKEN")}`,
    Accept: "application/json; charset=utf-8",
    "Content-Type": "application/json; charset=utf-8",
  };
  config.timeout = 120000;
  return config;
});
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      await authProvider.refresh({});
      return axiosInstance(originalRequest);
    }
    return Promise.reject(error);
  }
);

interface ExtraRow {
  client: string;
  suite: string;
  extra: string;
  code: string | null;
  amount: number;
  qty: number;
  dated: boolean;
}
interface RecapRow {
  extra: string;
  count: number;
  total: number;
}
interface ExtrasData {
  date: string;
  extras: ExtraRow[];
  recap: RecapRow[];
  total: number;
  count: number;
}

const FMT = "YYYY-MM-DD";

export const ExtrasPlanningList: React.FC<IResourceComponentsProps> = () => {
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [data, setData] = useState<ExtrasData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchData = useCallback(async (m: Dayjs) => {
    setLoading(true);
    try {
      const apiUrl = process.env?.REACT_APP_API_URL || "";
      const res = await axiosInstance.get(
        `${apiUrl}/api/extras-planning?date=${m.format(FMT)}`
      );
      setData(res.data?.data ?? null);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(date);
  }, [date, fetchData]);

  const columns = [
    { title: "Suite", dataIndex: "suite", key: "suite" },
    { title: "Client", dataIndex: "client", key: "client" },
    {
      title: "Extra",
      dataIndex: "extra",
      key: "extra",
      render: (_: any, r: ExtraRow) => (
        <Space>
          <CoffeeOutlined style={{ color: "#8c8c8c" }} />
          <span>{r.qty > 1 ? `${r.extra} ×${r.qty}` : r.extra}</span>
          {r.amount === 0 && <Tag color="green">compris</Tag>}
        </Space>
      ),
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (v: number) => <Text strong>{v.toFixed(2)} €</Text>,
    },
  ];

  return (
    <List title="Planning extras">
      <Text type="secondary">
        Extras à réaliser à la date sélectionnée (petit-déjeuner compris affiché à 0&nbsp;€,
        extras annulés masqués).
      </Text>

      {/* Barre de navigation date */}
      <Row align="middle" gutter={8} style={{ margin: "16px 0" }}>
        <Col>
          <Button icon={<LeftOutlined />} onClick={() => setDate(date.subtract(1, "day"))}>
            Jour préc.
          </Button>
        </Col>
        <Col>
          <DatePicker
            value={date}
            allowClear={false}
            format="DD/MM/YYYY"
            onChange={(d) => d && setDate(d)}
          />
        </Col>
        <Col>
          <Button onClick={() => setDate(date.add(1, "day"))}>
            Jour suiv. <RightOutlined />
          </Button>
        </Col>
        <Col>
          <Button type="dashed" onClick={() => setDate(dayjs())}>
            Aujourd'hui
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <Row gutter={16}>
          <Col xs={24} lg={16}>
            <Table
              rowKey={(r, i) => `${r.suite}-${r.client}-${i}`}
              dataSource={data?.extras ?? []}
              columns={columns}
              pagination={false}
              locale={{ emptyText: "Aucun extra pour cette date." }}
              size="middle"
            />
          </Col>
          <Col xs={24} lg={8}>
            <Card title="Récapitulatif" size="small">
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                  Total du jour
                </Text>
                <Text strong style={{ fontSize: 24 }}>
                  {(data?.total ?? 0).toFixed(2)} €
                </Text>
              </div>
              {(data?.recap ?? []).map((r, i) => (
                <Row key={i} justify="space-between" style={{ padding: "4px 0" }}>
                  <Col>
                    <Text>{r.extra}</Text> <Tag>×{r.count}</Tag>
                  </Col>
                  <Col>
                    <Text strong>{r.total.toFixed(2)} €</Text>
                  </Col>
                </Row>
              ))}
              {!data?.recap?.length && <Text type="secondary">—</Text>}
            </Card>
          </Col>
        </Row>
      )}
    </List>
  );
};
